// "What we bought": the receipts in a date range, by person and context,
// checked against Chase, broken down by kind, store, person and item.
// One list call (with lines) per date range; the person / context / kind
// filters work on what came back. Coverage of Chase grocery spend (and the
// charges with no receipt) shows only when the server can answer it, by date
// range and person; nothing here is estimated. A payment (a tax payment, a
// fee, rent) is not a purchase: the spending sections run on purchases and
// payments have their own section; Checked against Chase covers both.
// The FIG card section covers the whole business card for the date range,
// whatever the person and context filters say, and shows even when the view
// has no receipts (a month of FIG charges and no receipts is its point).
import { useCallback, useEffect, useMemo, useState } from 'react';
import { receiptsCall } from './receiptsApi';
import { KIND_LABELS, KIND_COLORS, KIND_GROUPS } from './kindGroups';
import { filterReceipts, headline, byKind, realVsJunk, whoPaid, where, biggestItems, chaseBand, months, reconcile, taxBreakdown, splitPayments, paymentRows } from './receiptsModel';
import { matchTag } from './matchTag';
import { presetRange, summaryOf, sectionHeadlines, taxSentence, footSum } from './browseModel';
import CollapsibleSection from '../../common/CollapsibleSection';
import SummaryStrip from './SummaryStrip';
import RecentReceipts from './RecentReceipts';
import SendResult from './SendResult';
import MissingReceipts, { notShownLines } from './MissingReceipts';
import BackupBanner from './BackupBanner';
import FigCoverage from './FigCoverage';
import { FIG_COVERAGE_ACCOUNT, figSummary } from './figCoverageModel';
import { usd, monthLabel, shortDate } from './money';
import './Receipts.css';

const PEOPLE = [{ key: null, label: 'Everyone' }, { key: 'Marc', label: 'Marc' }, { key: 'Angela', label: 'Angela' }];
const CONTEXTS = ['Personal', 'FIG', 'Monduras', 'Split'];
const BAND_COLORS = { matched: '#2e8b57', noBank: '#a8a29a', waiting: '#e2c48a', notFound: '#c0392b' };
const pct = (x) => `${Math.round(x * 100)}%`;

// Dashboard date ranges (the browser adds Custom).
const PRESETS = [{ key: 'month', label: 'This month' }, { key: 'last', label: 'Last month' }, { key: 'quarter', label: 'This quarter' }, { key: 'ytd', label: 'Year to date' }, { key: 'all', label: 'All time' }];
// Sections open by default: the Chase band and By kind; the rest start closed.
// Each remembers its own state (CollapsibleSection, localStorage).
const OPEN_BY_DEFAULT = new Set(['payments', 'chase', 'kinds']);
const secId = (k) => `rc-dash-${k}`;

export default function WhatWeBought({ onBack, onOpenReceipt, onOpenAll, initialKind = null, groceriesCategoryId = null, backLabel = 'Finance', state = null, onState = null }) {
  // preset and person live with ReceiptsView when it hands them in, so Back returns to the same view.
  const [localPreset, setLocalPreset] = useState(state?.preset || 'month');
  const [localPerson, setLocalPerson] = useState(state?.person ?? null);
  const preset = localPreset; const person = localPerson;
  const setPreset = (v) => { setLocalPreset(v); if (onState) onState({ ...(state || {}), preset: v, person }); };
  const setPerson = (v) => { setLocalPerson(v); if (onState) onState({ ...(state || {}), preset, person: v }); };
  const [context, setContext] = useState(null);
  const [kind, setKind] = useState(initialKind);
  const [openKind, setOpenKind] = useState(null);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [coverage, setCoverage] = useState(null);
  const [fig, setFig] = useState(null);
  const [figTry, setFigTry] = useState(0);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(null);
  const [sendErr, setSendErr] = useState(null);
  const range = useMemo(() => presetRange(preset), [preset]);

  const load = useCallback(async () => {
    setErr(null); setData(null);
    try {
      const x = await receiptsCall('list', { ...(range.from ? { from: range.from } : {}), ...(range.to ? { to: range.to } : {}), withLines: true, limit: 200 });
      setData(x);
    } catch (e) { setErr(e.message); }
  }, [range]);
  useEffect(() => { load(); }, [load]);

  // Coverage and its missing list follow the date range and the person; one call answers both.
  useEffect(() => {
    let live = true;
    setCoverage(null);
    if (groceriesCategoryId && range.from && range.to) {
      receiptsCall('coverage', { category: groceriesCategoryId, from: range.from, to: range.to, ...(person ? { person } : {}) })
        .then((c) => { if (live) setCoverage(c); }, () => { if (live) setCoverage(null); });
    }
    return () => { live = false; };
  }, [range, person, groceriesCategoryId]);

  // The FIG card: by account, the date range only (no person, no context).
  useEffect(() => {
    let live = true;
    if (!range.from || !range.to) { setFig(null); return undefined; }
    setFig({ status: 'loading' });
    receiptsCall('coverage', { account: FIG_COVERAGE_ACCOUNT, from: range.from, to: range.to })
      .then((c) => { if (live) setFig({ status: 'ok', data: c }); }, (e) => { if (live) setFig({ status: 'error', error: e.message }); });
    return () => { live = false; };
  }, [range, figTry]);
  const figSection = (
    <CollapsibleSection key="fig" id={secId('fig')} title="FIG card ·8805" summary={fig?.status === 'ok' ? figSummary(fig.data) : null} defaultOpen className="rc-sec">
      <FigCoverage fig={fig} needsRange={!range.from || !range.to} onRetry={() => setFigTry((n) => n + 1)} onOpenReceipt={onOpenReceipt} />
    </CollapsibleSection>
  );

  const receipts = useMemo(() => filterReceipts(data?.receipts || [], { person, context, kind }), [data, person, context, kind]);
  // Spending is purchases; payments are listed on their own. The Chase band and the summary see both.
  const purchases = useMemo(() => splitPayments(receipts).purchases, [receipts]);
  const pays = useMemo(() => paymentRows(receipts), [receipts]);
  const h = useMemo(() => headline(purchases), [purchases]);
  const k = useMemo(() => byKind(purchases), [purchases]);
  const rj = useMemo(() => realVsJunk(purchases), [purchases]);
  const band = useMemo(() => chaseBand(receipts), [receipts]);
  const mo = useMemo(() => months(purchases), [purchases]);
  const rec = useMemo(() => reconcile(purchases), [purchases]);
  const summary = useMemo(() => summaryOf(receipts), [receipts]);
  const who = useMemo(() => whoPaid(purchases), [purchases]);
  const wh = useMemo(() => where(purchases), [purchases]);
  const big = useMemo(() => biggestItems(purchases), [purchases]);
  const tx = useMemo(() => taxBreakdown(purchases), [purchases]);
  const [openDetail, setOpenDetail] = useState(null);
  const heads = useMemo(() => sectionHeadlines({ band, kinds: k, rj, who, where: wh, items: big, receipts: receipts.length, months: mo, tax: tx, kindLabels: KIND_LABELS, payments: pays }), [band, k, rj, who, wh, big, receipts.length, mo, tx, pays]);
  const payTotal = pays.reduce((a, p) => a + Math.round((p.amount || 0) * 100), 0) / 100;
  const sec = (id, title, children) => (
    <CollapsibleSection key={id} id={secId(id)} title={title} summary={heads[id]} defaultOpen={OPEN_BY_DEFAULT.has(id)} className="rc-sec">{children}</CollapsibleSection>
  );

  const send = async (ids) => {
    setSending(true); setSendErr(null); setSent(null);
    try { setSent(await receiptsCall('send', { ids })); } catch (e) { setSendErr(e.message); }
    setSending(false);
  };

  const title = PRESETS.find((p) => p.key === preset)?.label;
  const year = new Date().getFullYear();
  const maxKind = Math.max(...k.rows.map((r) => r.amount), 0.01);
  const maxMonth = Math.max(...mo.map((m) => m.amount), 0.01);

  return (
    <div className="fin-root rc-page">
      <button className="rc-back" onClick={onBack}>← {backLabel}</button>
      <div className="rc-head">
        <div>
          <h2 className="rc-title">What we bought</h2>
          <div className="rc-sub">{title} · {data ? `${receipts.length} receipt${receipts.length === 1 ? '' : 's'}` : 'loading…'}{kind ? ` · only ${KIND_LABELS[kind]}` : ''}</div>
        </div>
      </div>
      <BackupBanner refused={data?.backupRefused} />
      <div className="rc-filters" style={{ marginBottom: '0.9rem' }}>
        {PEOPLE.map((p) => <button key={p.label} className={`rc-seg${person === p.key ? ' rc-seg--on' : ''}`} onClick={() => setPerson(p.key)}>{p.label}</button>)}
        <select className="rc-select" value={preset} onChange={(e) => setPreset(e.target.value)} aria-label="Dates">
          {PRESETS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
        </select>
        <select className="rc-select" value={context || ''} onChange={(e) => setContext(e.target.value || null)} aria-label="Context">
          <option value="">Any context</option>
          {CONTEXTS.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        {kind && <button className="rc-seg rc-seg--on" onClick={() => setKind(null)}>{KIND_LABELS[kind]} ✕</button>}
      </div>

      {err ? <div className="fin-state fin-state--error"><p>⚠ {err}</p><button className="fin-retry" onClick={load}>Retry</button></div>
        : !data ? <div className="fin-state"><div className="fin-spinner" /><p>Loading receipts…</p></div>
        : receipts.length === 0 ? <><div className="fin-state"><p>No receipts in this view.</p></div>{figSection}</>
        : (
          <>
            <SummaryStrip summary={summary} />

            {pays.length > 0 && sec('payments', 'Payments', (
              <>
                <div className="rc-scroll">
                  <table className="rc-table" style={{ minWidth: 0 }}>
                    <thead><tr><th>Payee</th><th>Date</th><th>Chase</th><th className="num">Amount</th></tr></thead>
                    <tbody>{pays.map((p) => {
                      const m = matchTag({ match: { status: p.matchStatus, candidates: [] }, total: p.total }, p.linkedAmount !== null && p.linkedAmount !== undefined ? { amount: p.linkedAmount, how: p.linkedHow, impliedTip: null } : null);
                      const account = [p.accountLast4 ? `·${p.accountLast4}` : null, p.owner].filter(Boolean).join(' ');
                      return (
                        <tr key={p.id}>
                          <td><button type="button" className="rc-anchor" onClick={() => onOpenReceipt(p.id)}>{p.payee}</button>
                            {p.purpose && <span className="rc-store-id">{p.purpose}</span>}
                            {account && <span className="rc-store-id">{account}</span>}</td>
                          <td className="rc-nowrap">{shortDate(p.date)}</td>
                          <td><span className={`rc-tag ${m.cls}`}>{m.text.replace(', to the penny', '')}</span></td>
                          <td className="num"><strong>{p.amount !== null ? usd(p.amount) : '—'}</strong></td>
                        </tr>
                      );
                    })}</tbody>
                  </table>
                </div>
                <p className="rc-note">Payments (taxes, fees, rent) are not purchases: they are left out of By kind, Who paid, Where, Biggest items, Tax and Month by month.</p>
              </>
            ))}

            {sec('chase', 'Checked against Chase', (
              <div className="rc-panel rc-panel--beige" style={{ marginBottom: 0 }}>
                <div style={{ fontSize: '0.8rem', color: '#5a3a10' }}>{band.segments[0].receipts} of {receipts.length} receipts match a charge</div>
                <div className="rc-band">{band.segments.filter((x) => x.amount > 0).map((x) => <span key={x.key} style={{ width: `${x.share * 100}%`, background: BAND_COLORS[x.key] }} title={`${x.label} ${usd(x.amount)}`} />)}</div>
                <div className="rc-legend">
                  {band.segments.filter((x) => x.receipts > 0).map((x) => (
                    <span key={x.key}><span className="rc-dot" style={{ background: BAND_COLORS[x.key] }} /><strong>{usd(x.amount)}</strong> {x.label} · {x.receipts}</span>
                  ))}
                </div>
                {coverage && coverage.charges > 0 && (
                  <p className="rc-note">{person ? `${person}'s grocery` : 'Grocery'} spend on Chase in this range: {usd(coverage.chargeDollars)} · receipts cover {pct(coverage.coveredShare ?? 0)} ({coverage.chargesWithReceipt} of {coverage.charges} charges).</p>
                )}
                <MissingReceipts coverage={coverage} />
                {coverage && notShownLines(coverage).length > 0 && (
                  <p className="rc-note">Not counted under {person}: {notShownLines(coverage).join('; ')}. Everyone shows them.</p>
                )}
              </div>
            ))}

            {figSection}

            <div className="rc-tiles">
              <div className="rc-tile rc-tile--ink">
                <div className="rc-tile-label">Purchases</div>
                <div className="rc-tile-value">{usd(h.total)}</div>
                <div className="rc-tile-sub">{h.receipts} receipt{h.receipts === 1 ? '' : 's'} · {h.stores} store{h.stores === 1 ? '' : 's'} · with tax{h.totalsMissing ? ` · ${h.totalsMissing} without a total` : ''}</div>
              </div>
              <div className="rc-tile rc-tile--junk">
                <div className="rc-tile-label">{KIND_LABELS.junk_food}</div>
                <div className="rc-tile-value">{usd(h.junk.amount)}</div>
                <div className="rc-tile-sub">{h.junk.items ? `${h.junk.details.slice(0, 3).join(', ') || 'items'} · ${h.junk.items} item${h.junk.items === 1 ? '' : 's'}` : 'none'} · before tax</div>
              </div>
            </div>

            {sec('kinds', 'By kind', (
              <>
                <div className="rc-bars">
                  {k.rows.map((row) => (
                    <div key={row.kind}>
                      <div className="rc-bar" onClick={() => setOpenKind((o) => (o === row.kind ? null : row.kind))} title="Show the detail">
                        <span>{KIND_LABELS[row.kind]}</span>
                        <span className="rc-bar-track"><span className="rc-bar-fill" style={{ display: 'block', width: `${(row.amount / maxKind) * 100}%`, background: KIND_COLORS[row.kind] }} /></span>
                        <span className="rc-bar-amt">{usd(row.amount)}<small>{pct(row.share)}</small></span>
                      </div>
                      {openKind === row.kind && (
                        <div className="rc-details">
                          {row.details.map((x) => {
                            const key = `${row.kind}|${x.detail}`;
                            const open = openDetail === key;
                            return (
                              <div key={x.detail}>
                                <div className="rc-row">
                                  <button type="button" className="rc-detail-btn" aria-expanded={open} onClick={() => setOpenDetail(open ? null : key)}>{open ? '▾' : '▸'} {x.detail} <span style={{ color: '#9a8a7a' }}>· {x.items.length}</span></button>
                                  <span>{usd(x.amount)}</span>
                                </div>
                                {open && (
                                  <div className="rc-detail-items">
                                    {x.items.map((it, i) => (
                                      <div className="rc-row" key={`${it.receiptId}-${i}`}>
                                        <span>{it.name} <span style={{ color: '#9a8a7a' }}>· {it.merchant || 'Merchant not read'} · {shortDate(it.date)}</span></span>
                                        <span className="rc-nowrap">{usd(it.amount)} <button type="button" className="rc-anchor" onClick={() => onOpenReceipt(it.receiptId)}>{it.receiptId}</button></span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                          {kind !== row.kind && <button className="rc-anchor" onClick={() => setKind(row.kind)}>Only {KIND_LABELS[row.kind]} on this page</button>}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <p className="rc-note">Items {usd(k.itemsTotal)}{tx.noTaxLine.receipts ? `, of which ${usd(tx.noTaxLine.total)} on ${tx.noTaxLine.receipts} receipt${tx.noTaxLine.receipts === 1 ? '' : 's'} with no tax line` : ''}.{k.unallocatedDiscount ? ` Savings on no single item: −${usd(k.unallocatedDiscount)}.` : ''} Unlabeled lines count as Other.{k.receiptsWithoutLines ? ` ${k.receiptsWithoutLines} receipt${k.receiptsWithoutLines === 1 ? '' : 's'} (${usd(k.dollarsWithoutLines)}) have no items read.` : ''}</p>
              </>
            ))}

            {(rj.real > 0 || rj.junk > 0) && sec('realJunk', 'Real food vs junk', (
              <>
                <div className="rc-split">
                  {rj.real > 0 && <span style={{ width: `${rj.realShare * 100}%`, background: '#2e8b57' }}>Real food {usd(rj.real)}</span>}
                  {rj.junk > 0 && <span style={{ width: `${rj.junkShare * 100}%`, background: KIND_COLORS.junk_food }}>{rj.junkShare > 0.3 ? `${KIND_LABELS.junk_food} ${usd(rj.junk)}` : rj.junkShare > 0.18 ? `Junk ${usd(rj.junk)}` : ''}</span>}
                </div>
                <p className="rc-note">
                  Real food: {KIND_GROUPS.real.map((x) => KIND_LABELS[x].toLowerCase()).join(', ')} · {pct(rj.realShare)}. {KIND_LABELS.junk_food}: {usd(rj.junk)} · {pct(rj.junkShare)}.
                  {rj.separate.length ? ` Counted on their own: ${rj.separate.map((x) => `${KIND_LABELS[x.kind]} ${usd(x.amount)}`).join(', ')}.` : ''}
                </p>
              </>
            ))}

            {sec('who', 'Who paid', (
              <div className="rc-cards">{who.map((p) => (
                <div className="rc-card" key={p.person}><div className="rc-card-name">{p.person}</div><div className="rc-card-value">{usd(p.amount)}</div><div className="rc-card-sub">{p.cards.map((t) => <div key={t}>{t}</div>)}{p.receipts} receipt{p.receipts === 1 ? '' : 's'}</div></div>
              ))}</div>
            ))}

            {sec('where', 'Where', (
              <div className="rc-cards">{wh.map((w) => (
                <div className="rc-card" key={w.merchant}><div className="rc-card-name">{w.merchant}</div><div className="rc-card-value">{usd(w.amount)}</div><div className="rc-card-sub">{w.trips} trip{w.trips === 1 ? '' : 's'}</div></div>
              ))}</div>
            ))}

            {sec('items', 'Biggest items', (
              <>
                <div className="rc-scroll">
                  <table className="rc-table" style={{ minWidth: 0 }}>
                    <thead><tr><th>Item</th><th>Kind</th><th className="rc-hide-sm">Store</th><th className="num">Spent</th></tr></thead>
                    <tbody>{big.map((it) => (
                      <tr key={`${it.merchant}|${it.name}`}>
                        <td>{it.name}{it.count > 1 ? ` ×${it.count}` : ''}</td>
                        <td><span className="rc-dot" style={{ background: KIND_COLORS[it.kind] }} />{KIND_LABELS[it.kind]}</td>
                        <td className="rc-hide-sm">{it.receiptIds.length === 1 ? <button className="rc-anchor" onClick={() => onOpenReceipt(it.receiptIds[0])}>{it.merchant}</button> : it.merchant}</td>
                        <td className="num"><strong>{usd(it.amount)}</strong></td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
                <p className="rc-note">Before tax.</p>
              </>
            ))}

            {sec('receipts', 'Receipts', (
              <>
                {sent && <SendResult result={sent} onClose={() => setSent(null)} />}
                {sendErr && <p className="rc-error">⚠ {sendErr}</p>}
                <RecentReceipts rows={receipts.slice(0, 5)} onOpen={onOpenReceipt} onSend={send} busy={sending} />
                {onOpenAll && <div style={{ marginTop: '0.6rem' }}><button className="rc-btn rc-btn--primary" onClick={() => onOpenAll({ preset, person, ...(context ? { context } : {}) })}>All receipts{receipts.length > 5 ? ` (${receipts.length})` : ''} →</button></div>}
              </>
            ))}

            {sec('tax', 'Tax', (
              <>
                <p className="rc-note" style={{ marginTop: 0 }}>
                  {taxSentence(tx)}
                  {tx.noTaxLine.receipts ? ` ${tx.noTaxLine.receipts} with no tax line (${usd(tx.noTaxLine.total)}) are left out of the percent.` : ''}
                  {tx.noTotal ? ` ${tx.noTotal} without a total are left out.` : ''}
                </p>
                <div className="rc-scroll" style={{ marginBottom: '0.8rem' }}>
                  <table className="rc-table" style={{ minWidth: 0 }}>
                    <thead><tr><th>Store</th><th className="num">Items</th><th className="num">Tax</th><th className="num">Rate</th></tr></thead>
                    <tbody>{tx.byStore.map((s) => (
                      <tr key={s.merchant}>
                        <td>{s.merchant}<span className="rc-store-id">{s.receipts} with tax{s.noTaxLine ? ` · ${s.noTaxLine} no tax line` : ''}</span></td>
                        <td className="num">{s.receipts ? usd(s.items) : '—'}</td>
                        <td className="num">{s.receipts ? usd(s.tax) : <span className="rc-notax">no tax line</span>}</td>
                        <td className="num">{s.rate !== null ? `${(Math.round(s.rate * 1000) / 10).toFixed(1)}%` : '—'}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
                <div className="rc-scroll">
                  <table className="rc-table" style={{ minWidth: 0 }}>
                    <thead><tr><th>Receipt</th><th className="num">Total</th><th className="num">Tax</th><th className="num">Rate</th></tr></thead>
                    <tbody>{tx.byReceipt.map((r) => (
                      <tr key={r.id}>
                        <td><button type="button" className="rc-anchor" onClick={() => onOpenReceipt(r.id)}>{r.merchant || 'Merchant not read'}</button><span className="rc-store-id">{shortDate(r.date)} · {r.id}</span></td>
                        <td className="num">{usd(r.total)}</td>
                        <td className="num">{r.tax !== null ? usd(r.tax) : <span className="rc-notax">no tax line</span>}</td>
                        <td className="num">{r.rate !== null ? `${(Math.round(r.rate * 1000) / 10).toFixed(1)}%` : '—'}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              </>
            ))}

            {mo.length > 0 && sec('months', 'Month by month', (
              <div className="rc-months">{mo.map((m) => (
                <div className="rc-month" key={m.month}>
                  <strong>{usd(Math.round(m.amount))}</strong>
                  <div className="rc-month-bar" style={{ height: `${Math.max(6, (m.amount / maxMonth) * 100)}px` }} />
                  <span>{monthLabel(m.month, year)}</span>
                </div>
              ))}</div>
            ))}

            <div className="rc-foot">
              {footSum(rec, tx)}
              {Math.abs(rec.sum - rec.onReceipts) > 0.005 ? ` (receipt totals ${usd(rec.onReceipts)}${rec.noLines.length ? `; ${rec.noLines.length} without items` : ''}${rec.off.length ? `; ${rec.off.join(', ')} do not add up` : ''})` : ''}.
              {pays.length ? ` Payments ${usd(payTotal)} (${pays.length}) are not purchases and are not in this sum.` : ''}
              <br />Shows what was bought, not who ate it. Only knows receipts that were captured. Covers {receipts.length} receipt{receipts.length === 1 ? '' : 's'}{range.from ? `, ${range.from} to ${range.to}` : ', all time'}.</div>
          </>
        )}
    </div>
  );
}
